//
// # Empty Space Context Menu
//
// Displays actions when right-clicking on empty space in schematics, layouts, and symbols

import type { Editor } from "./editor";
import { ContentKind } from "DrawCore/src/content/kind";
import { DrawWire } from "EditorCore/src/content/schematic/modes/draw_wire";
import { Palette } from "DrawCore/src/style";
import { BaseContextMenu } from "./baseContextMenu";
import type { ContextMenuStyleConfig } from "./baseContextMenu";

export class EmptySpaceContextMenu extends BaseContextMenu {
  // Store the click position for creating instances at the right location
  private clickPosition: { x: number; y: number } | null = null;

  constructor(editor: Editor) {
    super(editor);
  }

  // -- Style config --

  readonly styleConfig: ContextMenuStyleConfig = {
    minWidth: "150px",
    maxWidth: "300px",
  };

  // -- Subclass stylesheet --

  protected getSubclassStyleSheet(): string {
    const p = Palette.getAll();
    return `
      .context-menu-header-light {
        padding: 6px 12px;
        color: ${p.textTarget};
        font-size: 11px;
      }
    `;
  }

  // -- clearState --

  protected clearState(): void {
    this.clickPosition = null;
  }

  // -- Show --

  // Show context menu at the given position
  // schX, schY are the schematic/layout coordinates (grid-snapped)
  show = (pageX: number, pageY: number, schX: number, schY: number): void => {
    // Close any other open context menus first
    this.editor.hideAllContextMenus();

    this.clickPosition = { x: schX, y: schY };

    // Create or update menu element
    this.ensureMenuElement();

    // Build menu content based on content type
    const content = this.buildMenuContent();

    this.displayMenu(content, pageX, pageY);

    // Set up click handlers for menu items
    this.setupMenuItemHandlers();
  };

  // Build the "Open Parent" menu item, enabled only when a breadcrumb exists.
  private getOpenParentMenuItem(): string {
    const cssClass = this.editor.platform.hasParentBreadcrumb()
      ? "context-menu-item"
      : "context-menu-item context-menu-item-disabled";
    return `<div class="${cssClass}" data-action="open-parent">Open Parent</div>`;
  }

  // Build the "Open Schematic" menu item (symbol designs only), enabled only
  // when the symbol has at least one linked schematic implementation.
  private getOpenSchematicMenuItem(): string {
    const content = this.editor.content;
    const enabled =
      content.contentKind === ContentKind.Symbol &&
      !!content.render.schematicImpls &&
      content.render.schematicImpls.length > 0;
    const cssClass = enabled
      ? "context-menu-item"
      : "context-menu-item context-menu-item-disabled";
    return `<div class="${cssClass}" data-action="open-schematic">Open Schematic</div>`;
  }

  // Build the "Open Symbol" menu item (schematic designs only), enabled only
  // when the schematic records the symbol it implements (`symbolImpl`).
  private getOpenSymbolMenuItem(): string {
    const content = this.editor.content;
    const enabled =
      content.contentKind === ContentKind.Schematic && !!content.symbolImpl;
    const cssClass = enabled
      ? "context-menu-item"
      : "context-menu-item context-menu-item-disabled";
    return `<div class="${cssClass}" data-action="open-symbol">Open Symbol</div>`;
  }

  // Build the HTML content for the menu
  private buildMenuContent = (): string => {
    const contentKind = this.editor.content.contentKind;
    // OpenAccess designs (libcellview idents) reveal in the OpenAccess tree, not
    // the Depot — label the reveal action by the current design's ident kind.
    const viewInLabel =
      this.editor.content.getIdent().kind === "libcellview"
        ? "View in OpenAccess"
        : "View in Depot";
    const posText = this.clickPosition
      ? `(${this.clickPosition.x.toFixed(1)}, ${this.clickPosition.y.toFixed(
          1
        )})`
      : "";

    let content = `<div class="context-menu-info">`;
    content += `<div class="context-menu-header-light">Position: ${posText}</div>`;
    content += `</div>`;

    // Add separator
    content += `<div class="context-menu-separator"></div>`;

    // Add action items based on content type
    content += `<div class="context-menu-actions">`;

    if (contentKind === ContentKind.Schematic) {
      content += `<div class="context-menu-item" data-action="add-instance">Add Instance</div>`;
      content += `<div class="context-menu-item" data-action="add-pin">Add Pin</div>`;
      content += `<div class="context-menu-item" data-action="draw-wire">Draw Wire</div>`;
      content += `<div class="context-menu-item" data-action="add-text">Add Text...</div>`;
      content += `<div class="context-menu-separator"></div>`;
      content += `<div class="context-menu-item" data-action="view-in-depot">${viewInLabel}</div>`;
      content += this.getOpenParentMenuItem();
      content += this.getOpenSymbolMenuItem();
      content += `<div class="context-menu-item" data-action="generate-from-source">Layout: Generate From Source</div>`;
      content += `<div class="context-menu-separator"></div>`;
      content += `<div class="context-menu-item" data-action="save-as">Save As...</div>`;
    } else if (contentKind === ContentKind.Layout) {
      content += `<div class="context-menu-item" data-action="add-instance">Add Instance</div>`;
      content += `<div class="context-menu-item" data-action="add-layout-pin">Add Layout Pin</div>`;
      // Blockages are system-managed under the v2 redesign; no
      // user-facing "Add Blockage" affordance.
      content += `<div class="context-menu-separator"></div>`;
      content += `<div class="context-menu-item" data-action="view-in-depot">${viewInLabel}</div>`;
      content += this.getOpenParentMenuItem();
      content += `<div class="context-menu-item" data-action="configure-power-routing">Configure Power Routing...</div>`;
      content += `<div class="context-menu-separator"></div>`;
      content += `<div class="context-menu-item" data-action="save-as">Save As...</div>`;
    } else if (contentKind === ContentKind.Symbol) {
      content += `<div class="context-menu-item" data-action="add-pin">Add Pin</div>`;
      content += `<div class="context-menu-item" data-action="add-expr-label">Add Expression Label</div>`;
      content += `<div class="context-menu-item" data-action="add-selection-box">Add Selection Box</div>`;
      content += `<div class="context-menu-separator"></div>`;
      content += `<div class="context-menu-item" data-action="draw-rect">Draw Rectangle</div>`;
      content += `<div class="context-menu-item" data-action="draw-polygon">Draw Polygon</div>`;
      content += `<div class="context-menu-item" data-action="draw-path">Draw Path</div>`;
      content += `<div class="context-menu-item" data-action="draw-ellipse">Draw Ellipse</div>`;
      content += `<div class="context-menu-item" data-action="draw-arc">Draw Arc</div>`;
      content += `<div class="context-menu-separator"></div>`;
      content += `<div class="context-menu-item" data-action="graphics-catalogue">Add Graphics from Catalog</div>`;
      content += `<div class="context-menu-separator"></div>`;
      content += `<div class="context-menu-item" data-action="view-in-depot">${viewInLabel}</div>`;
      content += this.getOpenParentMenuItem();
      content += this.getOpenSchematicMenuItem();
      content += `<div class="context-menu-separator"></div>`;
      content += `<div class="context-menu-item" data-action="save-as">Save As...</div>`;
    }

    content += `</div>`;

    return content;
  };

  // Set up click handlers for menu items
  private setupMenuItemHandlers = (): void => {
    if (!this.menuElement) return;

    const menuItems = this.menuElement.querySelectorAll(".context-menu-item");
    menuItems.forEach((item) => {
      const element = item as HTMLElement;
      element.onclick = (e) => {
        e.stopPropagation();
        const action = element.getAttribute("data-action");

        if (action === "add-instance") {
          this.handleAddInstance();
        } else if (action === "add-pin") {
          this.handleAddPin();
        } else if (action === "draw-wire") {
          this.handleDrawWire();
        } else if (action === "add-text") {
          this.handleAddText();
        } else if (action === "view-in-depot") {
          this.handleViewInDepot();
        } else if (action === "generate-from-source") {
          this.handleGenerateFromSource();
        } else if (action === "open-parent") {
          this.handleOpenParent();
        } else if (action === "open-schematic") {
          this.handleOpenSchematic();
        } else if (action === "open-symbol") {
          this.handleOpenSymbol();
        } else if (action === "configure-power-routing") {
          this.handleConfigurePowerRouting();
        } else if (action === "add-layout-pin") {
          this.handleAddLayoutPin();
        } else if (action === "add-expr-label") {
          this.handleAddExprLabel();
        } else if (action === "add-selection-box") {
          this.handleAddSelectionBox();
        } else if (action === "draw-rect") {
          this.handleDrawRect();
        } else if (action === "draw-polygon") {
          this.handleDrawPolygon();
        } else if (action === "draw-path") {
          this.handleDrawPath();
        } else if (action === "draw-ellipse") {
          this.handleDrawEllipse();
        } else if (action === "draw-arc") {
          this.handleDrawArc();
        } else if (action === "graphics-catalogue") {
          this.handleGraphicsCatalogue();
        } else if (action === "save-as") {
          this.handleSaveAs();
        }

        this.hide();
      };
    });
  };

  // Handle add instance action - sends message to VSCode extension to show symbol picker and param form
  private handleAddInstance = (): void => {
    const { editor, clickPosition } = this;
    if (!clickPosition) return;

    // Notify platform to trigger addInstanceAtPosition command
    // This will show the symbol picker QuickPick and parameter form if needed
    editor.platform.notifyAddInstanceAtPosition(clickPosition);
  };

  // Handle add pin action - schematic opens dialog, symbol uses its own dialog
  private handleAddPin = (): void => {
    const { editor, clickPosition } = this;
    if (!clickPosition) return;

    const contentKind = editor.content.contentKind;
    if (contentKind === ContentKind.Schematic) {
      // Schematic: open the Add Pin dialog
      editor.platform.notifyAddPinAtPosition(clickPosition);
    } else if (contentKind === ContentKind.Symbol) {
      // Symbol: use the symbol-specific pin dialog
      editor.platform.notifyAddSymbolPin(clickPosition);
    }
  };

  // Handle add layout pin action (layout only) - opens dialog via platform
  private handleAddLayoutPin = (): void => {
    const { editor, clickPosition } = this;
    if (!clickPosition) return;

    const contentKind = editor.content.contentKind;
    if (contentKind !== ContentKind.Layout) return;

    editor.platform.notifyAddLayoutPin(clickPosition);
  };

  // Handle add text annotation action (schematic only) - opens dialog via platform.
  private handleAddText = (): void => {
    const { editor, clickPosition } = this;
    if (!clickPosition) return;

    if (editor.content.contentKind !== ContentKind.Schematic) return;

    editor.platform.notifyAddTextAnnotation(clickPosition);
  };

  // Handle add expression label action (symbol only) - opens dialog via platform
  private handleAddExprLabel = (): void => {
    const { editor, clickPosition } = this;
    if (!clickPosition) return;

    const contentKind = editor.content.contentKind;
    if (contentKind !== ContentKind.Symbol) return;

    editor.platform.notifyAddExprLabel(clickPosition);
  };

  // Handle add selection box action (symbol only) - enters draw mode.
  private handleAddSelectionBox = (): void => {
    const { editor } = this;
    if (editor.content.contentKind !== ContentKind.Symbol) return;
    editor.content.startAddSelectionBox();
  };

  // Handle draw rectangle action (symbol only) - enters annotation draw rect mode
  private handleDrawRect = (): void => {
    const { editor } = this;
    if (editor.content.contentKind !== ContentKind.Symbol) return;
    editor.content.annotations.startAddRect();
  };

  // Handle draw polygon action (symbol only) - enters annotation draw polygon mode
  private handleDrawPolygon = (): void => {
    const { editor } = this;
    if (editor.content.contentKind !== ContentKind.Symbol) return;
    editor.content.annotations.startAddPolygon();
  };

  // Handle draw path action (symbol only) - enters annotation draw path mode
  private handleDrawPath = (): void => {
    const { editor } = this;
    if (editor.content.contentKind !== ContentKind.Symbol) return;
    editor.content.annotations.startAddPath();
  };

  // Handle draw ellipse action (symbol only) - enters annotation draw ellipse mode
  private handleDrawEllipse = (): void => {
    const { editor } = this;
    if (editor.content.contentKind !== ContentKind.Symbol) return;
    editor.content.annotations.startAddEllipse();
  };

  // Handle draw arc action (symbol only) - enters annotation draw arc mode
  private handleDrawArc = (): void => {
    const { editor } = this;
    if (editor.content.contentKind !== ContentKind.Symbol) return;
    editor.content.annotations.startAddArc();
  };

  // Handle graphics catalogue action (symbol only) - opens catalogue dialog via platform
  private handleGraphicsCatalogue = (): void => {
    const { editor } = this;
    if (editor.content.contentKind !== ContentKind.Symbol) return;
    editor.platform.notifyOpenGraphicsCatalogue();
  };

  // Handle draw wire action (schematic only) - directly enters draw wire mode
  private handleDrawWire = (): void => {
    const { editor, clickPosition } = this;
    if (!clickPosition) return;

    // Check contentKind inline to let TypeScript narrow the type.
    if (editor.content.contentKind !== ContentKind.Schematic) return;

    editor.setMode(DrawWire.start(editor.content, clickPosition));
  };

  // Handle view in depot action - reveal the current design in the Depot tree
  private handleViewInDepot = (): void => {
    this.editor.platform.notifyViewCurrentDesignInDepot();
  };

  // Handle generate layout action (schematic only) - sends message to VSCode extension
  private handleGenerateFromSource = (): void => {
    const { editor } = this;

    // Check contentKind inline to let TypeScript narrow the type.
    if (editor.content.contentKind !== ContentKind.Schematic) return;

    // Notify platform to trigger generateFromSource command
    editor.platform.notifyGenerateFromSourceRequest();
  };

  // Handle open parent action - navigate back to the design this was opened from
  private handleOpenParent = (): void => {
    this.editor.platform.notifyOpenParent();
  };

  // Handle open schematic action (symbol only) - navigate to the schematic(s)
  // implementing this symbol.
  private handleOpenSchematic = (): void => {
    if (this.editor.content.contentKind !== ContentKind.Symbol) return;
    this.editor.platform.notifyOpenLinkedSchematic();
  };

  // Handle open symbol action (schematic only) - navigate to the symbol this
  // schematic implements.
  private handleOpenSymbol = (): void => {
    if (this.editor.content.contentKind !== ContentKind.Schematic) return;
    this.editor.platform.notifyOpenLinkedSymbol();
  };

  // Handle configure power routing action (layout only) - opens dialog via platform
  private handleConfigurePowerRouting = (): void => {
    const { editor } = this;

    // Check contentKind inline to let TypeScript narrow the type.
    if (editor.content.contentKind !== ContentKind.Layout) return;

    // Notify platform to open power routing configuration dialog
    editor.platform.notifyConfigurePowerRouting();
  };

  // Handle save as action - opens the Save As dialog via platform
  private handleSaveAs = (): void => {
    this.editor.platform.notifySaveAsRequested();
  };
}
